import {
  batchRestResourceResolver,
  restResourceResolver,
} from './restResourceResolver/restResourceResolver';
import {
  announcementsResolver,
  dismissAnnouncement,
} from './resolvers/announcements';
import {
  archiveCard,
  changeCardDueDate,
  copyCard,
  createCard,
  createCardTemplate,
  deleteCard,
  possibleCardRoleResolver,
  removeMemberFromCard,
  unarchiveCard,
  updateCardCover,
  updateCardDates,
  updateCardDueComplete,
  updateCardList,
  updateCardName,
  updateCardRole,
  uploadCardCover,
} from './resolvers/card';
import {
  addChecklist,
  deleteChecklist,
  updateChecklistName,
  updateChecklistPos,
} from './resolvers/checklist';
import {
  addCheckItem,
  deleteCheckItem,
  updateCheckItemDueDate,
  updateCheckItemName,
  updateCheckItemPos,
  updateCheckItemState,
} from './resolvers/checkItem';
import { emailProviderResolver } from './resolvers/emailProvider';
import { createList } from './resolvers/list';
import {
  acceptDeveloperTerms,
  addBoardStar,
  addCampaign,
  addOneTimeMessagesDismissed,
  changeMemberEmail,
  deleteOneTimeMessagesDismissed,
  memberAgreementsResolver,
  memberAtlassianAccountsResolver,
  memberAtlassianOrganizationsResolver,
  memberAtlassianProfileResolver,
  memberCardsResolver,
  memberSearchResolver,
  memberGuestOrganizationsResolver,
  prepMemberForAtlassianAccountOnboarding,
  prepMemberForEmailHygiene,
  startAutomaticOnboarding,
  removeBoardStar,
  resendVerificationEmail,
  unblockMemberProfileSync,
  updateBoardStar,
  updateCampaign,
  updateMemberActiveChannel,
  updateMemberAvatarSource,
  updateTeamifyVoluntaryDone,
  updateUserLocale,
  uploadMemberAvatar,
} from './resolvers/member';
import {
  addFreeTrial,
  applyBCDiscount,
  organizationOwnedPluginsResolver,
  organizationStatsResolver,
  organizationCardStatsResolver,
  organizationLabelNamesResolver,
  organizationNewBillableGuestsResolver,
  createOrganization,
  copyBoardToOrg,
  deleteOrganizationLogo,
  addMemberToOrg,
  removeMemberFromWorkspace,
  updateOrganization,
  uploadOrganizationImage,
  organizationCardsResolver,
  organizationMemberCardsResolver,
  addTag,
  toggleAutoUpgrade,
  deleteOrganization,
  bulkDisableWorkspacePowerUp,
} from './resolvers/organization';
import {
  addMembersPriceQuotesResolver,
  cancelPaidAccountFactory,
  certCaptureTokenResolver,
  createWorkspacePaidAccount,
  newSubscriptionListPriceQuotesResolver,
  newSubscriptionPriceQuotesResolver,
  reactivateGoldPaidAccount,
  reactivateWorkspacePaidAccount,
  renewalPriceQuotesResolver,
  statementsResolver,
  updateBillingContactDetailsFactory,
  updateCreditCardFactory,
  updateInvoiceDetailsFactory,
  updatePaidProductFactory,
  upgradePriceQuotesResolver,
} from './resolvers/paidAccount';
import {
  pluginCategoriesResolver,
  publicPluginsResolver,
} from './resolvers/publicPlugin';
import { unsplashPhotosResolver } from './resolvers/unsplash';
import { addButlerButton, deleteButlerButton } from './resolvers/butlerButton';
import {
  enablePlugin,
  updateBoardOrg,
  updateBoardVisibility,
  updateBoardCardCoversPref,
  boardExportResolver,
  startBoardExport,
  statsResolver,
  boardDashboardViewTileResolver,
  updateCalendarKey,
  updateCalendarFeedEnabledPref,
  addMemberToBoard,
  removeMemberFromBoard,
  reopenBoard,
  deleteBoard,
  updateEnabledPluginBoardButtonShortcuts,
  updatePluginHeaderShortcutsEnabled,
  createBoardInviteSecret,
  disableBoardInviteSecret,
  boardInviteSecretRequestResolver,
  closeBoard,
} from './resolvers/board';
import {
  createCustomField,
  deleteCustomField,
  updateCustomField,
  deleteCustomFieldOption,
  addCustomFieldOption,
  updateCustomFieldOption,
  updateCustomFieldItem,
} from './resolvers/customFields';
import {
  addPluginCollaborator,
  createPlugin,
  createPluginListing,
  deletePlugin,
  deletePluginListing,
  pluginCollaboratorsResolver,
  removePluginCollaborator,
  updatePlugin,
  updatePluginListing,
} from './resolvers/plugin';
import {
  templateCategoriesResolver,
  templateGalleryResolver,
  templateLanguagesResolver,
} from './resolvers/template';
import {
  auditlogResolver,
  claimOrganization,
  deleteAllManagedMemberTokens,
  deleteManagedMemberTokens,
  enterpriseClaimableOrganizationsResolver,
  getEnterpriseExportResolver,
  linkEnterpriseWithAtlassianOrganization,
  managedMembersWithTokensResolver,
  startEnterpriseExport,
  transferrableDataForOrganizationResolver,
  updateEnterpriseApiTokenCreationPermission,
} from './resolvers/enterprise';
import {
  createDashboardViewTile,
  deleteDashboardViewTile,
  updateDashboardViewTile,
} from './resolvers/boardDashboard';
import {
  boardHistoryResolver,
  getCardsPerDueDateStatus,
  getCardsPerLabel,
  getCardsPerList,
  getCardsPerMember,
} from './resolvers/boardHistory';
import { qrCodeResolver } from './resolvers/qrCode';
import {
  createOrganizationView,
  deleteOrganizationView,
  updateOrganizationView,
  updateViewInOrganizationView,
} from './resolvers/organizationView';
import {
  notificationGroupsResolver,
  notificationsCountResolver,
  notificationsResolver,
  setAllNotificationsRead,
  setNotificationsRead,
} from './resolvers/notifications';
import {
  boardAccessRequestResolver,
  boardAccessVerificationRequestResolver,
  sendBoardAccessRequest,
} from './resolvers/requestAccess';
import {
  migrateEnterpriseToAtlassian,
  updateEnterpriseMultipleEmailMembersPrimaryEmail,
  enterpriseMultipleEmailMembersResolver,
} from './resolvers/migrateEnterprise';
import { Resolvers } from '@apollo/client';
import { trelloSlackAssociationResolver } from './resolvers/slack';

export const queryMap = {
  announcements: announcementsResolver,
  board: restResourceResolver,
  boardAccessRequest: boardAccessRequestResolver,
  boardAccessVerificationRequest: boardAccessVerificationRequestResolver,
  boards: batchRestResourceResolver,
  card: restResourceResolver,
  cards: batchRestResourceResolver,
  certCaptureToken: certCaptureTokenResolver,
  checklist: restResourceResolver,
  checklists: batchRestResourceResolver,
  invitationSecret: boardInviteSecretRequestResolver,
  list: restResourceResolver,
  lists: batchRestResourceResolver,
  atlassianAccounts: memberAtlassianAccountsResolver,
  atlassianProfile: memberAtlassianProfileResolver,
  emailProvider: emailProviderResolver,
  enterpriseMultipleEmailMembers: enterpriseMultipleEmailMembersResolver,
  member: restResourceResolver,
  members: batchRestResourceResolver,
  memberCards: memberCardsResolver,
  memberSearch: memberSearchResolver,
  notificationGroups: notificationGroupsResolver,
  notifications: notificationsResolver,
  notificationsCount: notificationsCountResolver,
  organization: restResourceResolver,
  organizations: batchRestResourceResolver,
  organizationMemberCards: organizationMemberCardsResolver,
  organizationView: restResourceResolver,
  organizationViews: batchRestResourceResolver,
  label: restResourceResolver,
  labels: batchRestResourceResolver,
  enterprise: restResourceResolver,
  enterprises: batchRestResourceResolver,
  publicPlugins: publicPluginsResolver,
  newSubscriptionListPriceQuotes: newSubscriptionListPriceQuotesResolver,
  newSubscriptionPriceQuotes: newSubscriptionPriceQuotesResolver,
  upgradePriceQuotes: upgradePriceQuotesResolver,
  renewalPriceQuotes: renewalPriceQuotesResolver,
  statements: statementsResolver,
  addMembersPriceQuotes: addMembersPriceQuotesResolver,
  search: restResourceResolver,
  plugin: restResourceResolver,
  plugins: batchRestResourceResolver,
  pluginCategories: pluginCategoriesResolver,
  customField: restResourceResolver,
  qrCode: qrCodeResolver,
  unsplashPhotos: unsplashPhotosResolver,
  templateCategories: templateCategoriesResolver,
  templateLanguages: templateLanguagesResolver,
  templateGallery: templateGalleryResolver,
  trelloSlackAssociation: trelloSlackAssociationResolver,
};

export const resolvers = {
  Query: queryMap,
  Mutation: {
    deleteOrganization,
    addButlerButton,
    addChecklist,
    addOneTimeMessagesDismissed,
    createOrganization,
    copyBoardToOrg,
    updateBoardOrg,
    updateBoardVisibility,
    updateBoardCardCoversPref,
    dismissAnnouncement,
    deleteChecklist,
    updateChecklistName,
    updateChecklistPos,
    addCheckItem,
    deleteCheckItem,
    addCampaign,
    updateCampaign,
    updateCheckItemDueDate,
    updateCheckItemName,
    updateCheckItemPos,
    updateCheckItemState,
    addBoardStar,
    removeBoardStar,
    updateBoardStar,
    updateUserLocale,
    updateTeamifyVoluntaryDone,
    updateOrganization,
    prepMemberForAtlassianAccountOnboarding,
    prepMemberForEmailHygiene,
    startAutomaticOnboarding,
    unblockMemberProfileSync,
    deleteOneTimeMessagesDismissed,
    createPlugin,
    deletePlugin,
    enablePlugin,
    updatePlugin,
    createPluginListing,
    updatePluginListing,
    deletePluginListing,
    addPluginCollaborator,
    removePluginCollaborator,
    acceptDeveloperTerms,
    updateMemberAvatarSource,
    updateCardCover,
    uploadCardCover,
    updateCardDueComplete,
    updateCardList,
    updateCardName,
    uploadMemberAvatar,
    changeMemberEmail,
    updateMemberActiveChannel,
    resendVerificationEmail,
    addMemberToOrg,
    removeMemberFromWorkspace,
    createList,
    createCardTemplate,
    createCard,
    copyCard,
    archiveCard,
    unarchiveCard,
    deleteCard,
    changeCardDueDate,
    updateCardDates,
    updateCustomFieldItem,
    removeMemberFromCard,
    startBoardExport,
    startEnterpriseExport,
    claimOrganization,
    createCustomField,
    deleteCustomField,
    updateCustomField,
    deleteCustomFieldOption,
    addCustomFieldOption,
    updateCustomFieldOption,
    linkEnterpriseWithAtlassianOrganization,
    createWorkspacePaidAccount,
    reactivateWorkspacePaidAccount,
    reactivateGoldPaidAccount,
    // eslint-disable-next-line @trello/no-module-logic
    updateBusinessClassCreditCard: updateCreditCardFactory('organizations'),
    // eslint-disable-next-line @trello/no-module-logic
    updateBusinessClassPaidProduct: updatePaidProductFactory('organizations'),
    // eslint-disable-next-line @trello/no-module-logic
    updateGoldPaidProduct: updatePaidProductFactory('members'),
    // eslint-disable-next-line @trello/no-module-logic
    updateBusinessClassBillingContactDetails: updateBillingContactDetailsFactory(
      'organizations',
    ),
    // eslint-disable-next-line @trello/no-module-logic
    updateGoldBillingContactDetails: updateBillingContactDetailsFactory(
      'members',
    ),
    // eslint-disable-next-line @trello/no-module-logic
    cancelWorkspacePaidAccount: cancelPaidAccountFactory('organizations'),
    // eslint-disable-next-line @trello/no-module-logic
    cancelGoldPaidAccount: cancelPaidAccountFactory('members'),
    // eslint-disable-next-line @trello/no-module-logic
    updateBusinessClassBillingInvoiceDetails: updateInvoiceDetailsFactory(
      'organizations',
    ),
    // eslint-disable-next-line @trello/no-module-logic
    updateGoldBillingInvoiceDetails: updateInvoiceDetailsFactory('members'),
    uploadOrganizationImage,
    deleteOrganizationLogo,
    addFreeTrial,
    toggleAutoUpgrade,
    applyBCDiscount,
    deleteManagedMemberTokens,
    deleteAllManagedMemberTokens,
    updateEnterpriseApiTokenCreationPermission,
    createDashboardViewTile,
    updateDashboardViewTile,
    deleteDashboardViewTile,
    updateCardRole,
    createOrganizationView,
    updateOrganizationView,
    updateViewInOrganizationView,
    deleteOrganizationView,
    updateCalendarKey,
    updateCalendarFeedEnabledPref,
    addMemberToBoard,
    removeMemberFromBoard,
    addTag,
    setNotificationsRead,
    setAllNotificationsRead,
    deleteButlerButton,
    sendBoardAccessRequest,
    reopenBoard,
    migrateEnterpriseToAtlassian,
    deleteBoard,
    updateEnabledPluginBoardButtonShortcuts,
    updatePluginHeaderShortcutsEnabled,
    updateEnterpriseMultipleEmailMembersPrimaryEmail,
    bulkDisableWorkspacePowerUp,
    createBoardInviteSecret,
    disableBoardInviteSecret,
    closeBoard,
  },

  // Custom type resolvers

  Board: {
    export: boardExportResolver,
    exports: boardExportResolver,
    stats: statsResolver,
    dashboardViewTile: boardDashboardViewTileResolver,
    history: boardHistoryResolver,
  },

  Board_History: {
    cardsPerList: getCardsPerList,
    cardsPerLabel: getCardsPerLabel,
    cardsPerMember: getCardsPerMember,
    cardsPerDueDateStatus: getCardsPerDueDateStatus,
  },

  Card: {
    possibleCardRole: possibleCardRoleResolver,
  },

  Enterprise: {
    claimableOrganizations: enterpriseClaimableOrganizationsResolver,
    transferrableData: transferrableDataForOrganizationResolver,
    managedMembersWithTokens: managedMembersWithTokensResolver,
    auditlog: auditlogResolver,
    export: getEnterpriseExportResolver,
  },

  Member: {
    agreements: memberAgreementsResolver,
    atlassianOrganizations: memberAtlassianOrganizationsResolver,
    guestOrganizations: memberGuestOrganizationsResolver,
  },

  Organization: {
    ownedPlugins: organizationOwnedPluginsResolver,
    stats: organizationStatsResolver,
    newBillableGuests: organizationNewBillableGuestsResolver,
    cards: organizationCardsResolver,
  },

  Organization_Stats: {
    cards: organizationCardStatsResolver,
    labelNames: organizationLabelNamesResolver,
  },

  Plugin: {
    collaborators: pluginCollaboratorsResolver,
  },
} as Resolvers;
